import apiClient from '../../api/apiClient';
import { User, UserCreate, UserUpdate, Role } from './usersSlice';

// API URL'ini çevre değişkenlerinden al veya varsayılan değeri kullan
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
const USERS_ENDPOINT = '/users';

// Kullanıcı servisi
const usersService = {
  // Kullanıcıları getir
  // Not: Backend şu an toplam kayıt sayısını dönmediği için pagination'ı
  // frontend tarafında yapıyoruz. Tüm filtrelenmiş kullanıcıları çekip
  // component içinde slice ile sayfalıyoruz.
  getUsers: async (params: {
    page?: number;
    limit?: number;
    status?: string;
    role_id?: number;
    company_id?: number;
    search?: string;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }) => {
    const { page = 1, limit = 10, status, role_id, company_id, search, sort_by, sort_order } = params;

    // Filtre parametrelerini oluştur
    const queryParams = new URLSearchParams();

    // Pagination parameters
    const skip = (page - 1) * limit;
    queryParams.append("skip", skip.toString());
    queryParams.append("limit", limit.toString());

    if (status) queryParams.append("status", status);
    if (role_id !== undefined) queryParams.append("role_id", role_id.toString());
    if (company_id !== undefined)
      queryParams.append("company_id", company_id.toString());
    if (search) queryParams.append("search", search);
    if (sort_by) queryParams.append("sort_by", sort_by);
    if (sort_order) queryParams.append("sort_order", sort_order);

    const queryString = queryParams.toString();
    const url =
      queryString.length > 0
        ? `${USERS_ENDPOINT}?${queryString}`
        : USERS_ENDPOINT;

    const response = await apiClient.get(url);
    
    // Backend'den dönen format: { users: User[], total: number }
    const data = response as { users: User[], total: number };

    return {
      users: data.users,
      total: data.total,
      page,
      limit,
    };
  },
  
  // Kullanıcı detaylarını getir
  getUserById: async (userId: number): Promise<User> => {
    return await apiClient.get(`${USERS_ENDPOINT}/${userId}`);
  },
  
  // Yeni kullanıcı oluştur
  createUser: async (userData: UserCreate): Promise<User> => {
    return await apiClient.post(USERS_ENDPOINT, userData);
  },
  
  // Kullanıcı güncelle
  updateUser: async (userId: number, userData: UserUpdate): Promise<User> => {
    return await apiClient.put(`${USERS_ENDPOINT}/${userId}`, userData);
  },
  
  // Kullanıcı sil
  deleteUser: async (userId: number): Promise<void> => {
    return await apiClient.delete(`${USERS_ENDPOINT}/${userId}`);
  },

  // Agency Owner'ın marketing specialist'larını getir
  getMyAgencyMarketingSpecialists: async (): Promise<User[]> => {
    return await apiClient.get(`${USERS_ENDPOINT}/my-agency/marketing-specialists`);
  },

  // Agency Owner'ın tüm şirketlerindeki kullanıcıları getir
  getMyCompaniesUsers: async (): Promise<User[]> => {
    return await apiClient.get(`${USERS_ENDPOINT}/my-companies/users`);
  },

  // Agency'deki tüm kullanıcıları getir (listeleme için)
  getAgencyUsers: async (agencyId: number): Promise<User[]> => {
    return await apiClient.get(`${USERS_ENDPOINT}/agency/${agencyId}/users`);
  },

  // Agency'deki kullanıcı detayını getir
  getAgencyUserDetail: async (agencyId: number, userId: number): Promise<User> => {
    return await apiClient.get(`${USERS_ENDPOINT}/agency/${agencyId}/users/${userId}`);
  },

  // Agency'deki kullanıcıyı güncelle
  updateAgencyUser: async (agencyId: number, userId: number, userData: any): Promise<User> => {
    return await apiClient.put(`${USERS_ENDPOINT}/agency/${agencyId}/users/${userId}`, userData);
  },

  // Agency'deki kullanıcıyı sil
  deleteAgencyUser: async (agencyId: number, userId: number): Promise<void> => {
    return await apiClient.delete(`${USERS_ENDPOINT}/agency/${agencyId}/users/${userId}`);
  },

  // Kullanıcıları şirkete ata
  assignUsersToCompany: async (companyId: number, userIds: number[]): Promise<User[]> => {
    return await apiClient.post(`${USERS_ENDPOINT}/assign-to-company`, {
      company_id: companyId,
      user_ids: userIds
    });
  },

  // Kullanıcı listesini Excel olarak dışa aktar (name, email, company, created_at, updated_at)
  exportUsers: async (params?: {
    status?: string;
    role_id?: number;
    company_id?: number;
    search?: string;
  }): Promise<void> => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.role_id !== undefined) queryParams.append('role_id', params.role_id.toString());
    if (params?.company_id !== undefined) queryParams.append('company_id', params.company_id.toString());
    if (params?.search) queryParams.append('search', params.search);
    const queryString = queryParams.toString();
    const url = queryString ? `${USERS_ENDPOINT}/export?${queryString}` : `${USERS_ENDPOINT}/export`;

    const blob = await apiClient.get<Blob>(url, { responseType: 'blob' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'users_export.xlsx';
    a.click();
    URL.revokeObjectURL(a.href);
  },

  // Rolleri getir
  getRoles: async (): Promise<Role[]> => {
    return await apiClient.get('/roles');
  },

  // Kullanıcının şirketlerini getir
  getMyCompanies: async (): Promise<any[]> => {
    return await apiClient.get('/companies/my-companies');
  }
};

export default usersService;